import {
  NormalizedAlert,
  AlertResponseMetadata,
  AlertSeverity,
} from '../types/alert';
import { AlertCache } from './alertCache';

interface CanadaBulletinFeature {
  id: string;
  type: string;
  geometry?: any;
  properties: {
    identifier: string;
    datetime: string;
    type: string;
    ttaaii: string;
    issuer_code: string;
    issuer_name?: string | null;
    issuing_office?: string | null;
    url: string;
  };
}

interface CanadaBulletinResponse {
  type: string;
  features: CanadaBulletinFeature[];
}

// Approximate coordinate centroids for major Canadian provinces
const PROVINCE_CENTROIDS: Record<string, [number, number]> = {
  ON: [51.2538, -85.3232],
  QC: [52.9399, -73.5491],
  BC: [53.7267, -127.6476],
  AB: [53.9333, -116.5765],
  MB: [53.7609, -98.8139],
  SK: [52.9399, -106.4509],
  NS: [44.682, -63.7443],
  NB: [46.5653, -66.4619],
  NL: [53.1355, -57.6604],
  PE: [46.5107, -63.4168],
  NT: [64.8255, -124.8457],
  YT: [64.2823, -135.0],
  NU: [70.2998, -83.1076],
};

export class CanadaAdapter {
  private static endpoint =
    'https://api.weather.gc.ca/collections/bulletins-realtime/items?f=json&limit=40';

  /**
   * Derive approximate severity and description from bulletin header
   */
  static deriveSeverityFromCode(code: string): AlertSeverity {
    const c = code.toUpperCase();
    if (c.startsWith('WW') || c.includes('WARN')) return 'Severe';
    if (c.startsWith('WS') || c.includes('WATCH')) return 'Moderate';
    return 'Minor';
  }

  static normalizeBulletin(feature: CanadaBulletinFeature): NormalizedAlert | null {
    if (!feature || !feature.properties) return null;

    const p = feature.properties;
    const severity = this.deriveSeverityFromCode(p.ttaaii || p.type || '');
    const dateStr = p.datetime || new Date().toISOString();

    const office = p.issuing_office || p.issuer_code || 'ECCC Weather Centre';
    const title = `Environment Canada Weather Bulletin (${p.ttaaii || p.type})`;

    // Default centroid in central Canada (Ottawa / Ontario)
    const centroid: [number, number] = [45.4215, -75.6972];

    return {
      id: `ca_${p.identifier.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
      event: title,
      headline: `Environment Canada Official Bulletin [${p.ttaaii || 'MET'}]`,
      description: `Official meteorological bulletin issued by Environment and Climate Change Canada (ECCC) office ${office}. Reference bulletin ID: ${p.identifier}.`,
      instruction:
        'Consult local Environment Canada radio and provincial emergency management broadcasts for local instructions.',
      severity,
      urgency: severity === 'Severe' ? 'Immediate' : 'Expected',
      certainty: 'Observed',
      senderName: `Environment and Climate Change Canada (${office})`,
      areaDesc: 'Canada Regional Meteorological Zone',
      geometry: {
        type: 'Point',
        coordinates: [centroid[1], centroid[0]],
      },
      centroid,
      effective: dateStr,
      onset: dateStr,
      expires: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
      ends: null,
      status: 'Actual',
      messageType: 'Alert',
      category: 'Met',
      disruptionType: 'Weather',
      sourceAgency: 'ECCC',
      country: 'CA',
      sourceUrl: p.url || 'https://weather.gc.ca',
      firstSeen: dateStr,
      lastSeen: new Date().toISOString(),
    };
  }

  /**
   * Fetch live weather bulletins from Environment Canada GeoMet
   */
  static async fetchAlerts(options?: {
    forceRefresh?: boolean;
  }): Promise<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }> {
    const cacheKey = 'canada_eccc_bulletins';

    if (!options?.forceRefresh) {
      const cached = AlertCache.get<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }>(cacheKey);
      if (cached && !cached.isStale) {
        return {
          alerts: cached.data.alerts,
          metadata: {
            ...cached.data.metadata,
            isCached: true,
            cacheAgeSeconds: cached.ageSeconds,
          },
        };
      }
    }

    try {
      const res = await fetch(this.endpoint, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error(`Canada GeoMet HTTP ${res.status}: ${res.statusText}`);
      }

      const json: CanadaBulletinResponse = await res.json();
      const rawFeatures = Array.isArray(json.features) ? json.features : [];

      const normalized: NormalizedAlert[] = [];
      for (const feat of rawFeatures) {
        const norm = this.normalizeBulletin(feat);
        if (norm) normalized.push(norm);
      }

      const metadata: AlertResponseMetadata = {
        source: 'Environment and Climate Change Canada (weather.gc.ca)',
        totalAlerts: normalized.length,
        lastChecked: new Date().toISOString(),
        isCached: false,
        cacheAgeSeconds: 0,
        statusMessage: 'Verified live response from Environment Canada GeoMet',
      };

      AlertCache.set(cacheKey, { alerts: normalized, metadata });

      return { alerts: normalized, metadata };
    } catch (err: any) {
      const fallback = AlertCache.get<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }>(cacheKey);
      if (fallback) {
        return {
          alerts: fallback.data.alerts.map((a) => ({ ...a, isStale: true })),
          metadata: {
            ...fallback.data.metadata,
            isCached: true,
            isFallback: true,
            cacheAgeSeconds: fallback.ageSeconds,
            statusMessage: `Canada ECCC warning: ${err.message}. Showing cached data.`,
          },
        };
      }

      return {
        alerts: [],
        metadata: {
          source: 'Environment and Climate Change Canada (weather.gc.ca)',
          totalAlerts: 0,
          lastChecked: new Date().toISOString(),
          isCached: false,
          statusMessage: `Canada Feed Standby: ${err.message}`,
        },
      };
    }
  }
}

